import { useEffect, useMemo, useState } from "react";
import type { CoverView, InboxGroup } from "../api/connectionHub.types";
import { FAILURE_REASON, INBOX_PROVIDER } from "../api/connectionHub.types";
import type { RecheckPhase } from "../components/accounts/RecheckPanel/RecheckPanel";
import { CONNECTION_STATUS, isFailedStatus } from "../constants/connectionStatus.constants";

/** OTP-only channels have no password; an older API without usesPassword falls back to whether one was stored. */
export const takesPassword = (cover: CoverView): boolean => cover.usesPassword ?? cover.hasPassword;

export const isCredentialFailure = (cover: CoverView): boolean =>
  isFailedStatus(cover.status) && takesPassword(cover) && cover.failureReason === FAILURE_REASON.CREDENTIALS;

/** Gmail's forwarding check failed (or timed out): the normal forwarding step again, never the recheck. */
export const hasForwardingCheckFailed = (group: InboxGroup): boolean =>
  group.status === CONNECTION_STATUS.FORWARDING_VERIFICATION_FAILED ||
  group.covers.some((cover) => cover.status === CONNECTION_STATUS.FORWARDING_VERIFICATION_FAILED);

export interface Recheck {
  /** Null unless a channel on the inbox failed verification. */
  phase: RecheckPhase | null;
  /** The failed channels every recheck action is scoped to. */
  failedIds: string[];
  /** "I've Set Up Forwarding": straight to the filter step, with no API call. */
  skipForwarding: () => void;
}

/**
 * The recheck after a failed verification, driven by the channel status alone: a failed Gmail inbox
 * checks forwarding then the filter, a failed Outlook inbox checks its rule. The only state of its own
 * is whether the user kept their forwarding, which needs no server round trip.
 */
export const useRecheck = (
  group: InboxGroup,
  enabled = true,
  recheckable: (cover: CoverView) => boolean = () => true
): Recheck => {
  const [forwardingSkipped, setForwardingSkipped] = useState(false);
  const failedIds = useMemo(
    () => group.covers
      .filter((cover) => isFailedStatus(cover.status) && recheckable(cover))
      .map((cover) => cover.connectionId),
    [group.covers, recheckable]
  );
  const failed = failedIds.length > 0;

  // A fixed (or newly failed) inbox starts the recheck from forwarding again.
  useEffect(() => { if (!failed) setForwardingSkipped(false); }, [failed]);

  const provider = group.inbox.provider;
  const phase: RecheckPhase | null = !enabled || !failed || hasForwardingCheckFailed(group)
    ? null
    : provider === INBOX_PROVIDER.OUTLOOK
      ? "rule"
      : provider === INBOX_PROVIDER.GMAIL
        ? (forwardingSkipped ? "filter" : "forwarding")
        : null;

  return { phase, failedIds, skipForwarding: () => setForwardingSkipped(true) };
};
